import { List } from "lucide-react";
import "./blog.css";
import { posts, type BlogPost, type Span, type Cell } from "./posts";

function renderSpans(spans: Span[]) {
  return spans.map((s, i) => {
    if (s.kind === "text") return <span key={i}>{s.text}</span>;
    if (s.kind === "code") return <code key={i}>{s.text}</code>;
    return (
      <a
        key={i}
        className="blog__link"
        href={s.href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {s.label}
      </a>
    );
  });
}

function renderCell(cell: Cell) {
  if (cell.kind === "code") return <code>{cell.text}</code>;
  if (cell.kind === "num")
    return <span className="blog__num">{cell.text}</span>;
  return <>{cell.text}</>;
}

type Props = {
  activePostId?: string;
  onSelectPost: (id: string) => void;
  onBack: () => void;
};

export function BlogView({ activePostId, onSelectPost, onBack }: Props) {
  const post = posts.find((p) => p.id === activePostId) ?? posts[0];

  return (
    <div className="blog">
      <button
        type="button"
        className="blog__back"
        onClick={onBack}
        aria-label="Back to home"
      >
        <span aria-hidden="true">←</span>
        <span>back</span>
      </button>

      <nav className="blog__index" aria-label="Posts">
        <div className="blog__index-label">
          <List size={14} strokeWidth={1.75} aria-hidden="true" />
          <span>index</span>
        </div>
        {posts.map((p, i) => (
          <button
            key={p.id}
            type="button"
            className="blog__index-item"
            data-active={p.id === post.id}
            onClick={() => onSelectPost(p.id)}
          >
            <span className="blog__index-num">
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className="blog__index-title">{p.title}</span>
          </button>
        ))}
      </nav>

      <article className="blog__article">
        <Article post={post} />
      </article>
    </div>
  );
}

function Article({ post }: { post: BlogPost }) {
  return (
    <>
      <div className="blog__meta">{post.date}</div>
      <h1 className="blog__title">{post.title}</h1>
      <div className="blog__body">
        {post.body.map((block, i) => {
          if (block.kind === "p") return <p key={i}>{block.text}</p>;
          if (block.kind === "p-spans") return <p key={i}>{renderSpans(block.spans)}</p>;
          if (block.kind === "h2") return <h2 key={i}>{block.text}</h2>;
          if (block.kind === "ul") {
            return (
              <ul key={i} className="blog__list">
                {block.items.map((item, j) => (
                  <li key={j}>{item}</li>
                ))}
              </ul>
            );
          }
          if (block.kind === "ul-spans") {
            return (
              <ul key={i} className="blog__list">
                {block.items.map((spans, j) => (
                  <li key={j}>{renderSpans(spans)}</li>
                ))}
              </ul>
            );
          }
          return (
            <table key={i} className="blog__table">
              <thead>
                <tr>
                  {block.head.map((h, j) => (
                    <th key={j} data-col={j}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, j) => (
                  <tr key={j}>
                    {row.map((cell, k) => (
                      <td key={k} data-col={k} data-kind={cell.kind}>
                        {renderCell(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          );
        })}
      </div>
    </>
  );
}
